import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useTenant } from '@/hooks/useTenant';
import { useLanguage } from '@/hooks/useLanguage';
import { generateWhatsAppLink } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface FloatingWhatsAppButtonProps {
  /**
   * Custom message to pre-fill in WhatsApp chat
   * If not provided, uses default greeting message
   */
  message?: string;
  /**
   * Show/hide the button
   * @default true
   */
  show?: boolean;
  /**
   * Position from bottom (in pixels or tailwind class)
   * @default "bottom-6"
   */
  position?: string;
}

export const FloatingWhatsAppButton: React.FC<FloatingWhatsAppButtonProps> = ({
  message,
  show = true,
  position = 'bottom-6'
}) => {
  const { config } = useTenant();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Show button after 1 second to avoid being intrusive on page load
    const timer = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-show tooltip once after 3 seconds, then hide after 5 seconds
    if (isVisible) {
      const showTimer = setTimeout(() => setShowTooltip(true), 3000);
      const hideTimer = setTimeout(() => setShowTooltip(false), 8000);
      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isVisible]);

  if (!show || !config?.contact?.whatsapp) {
    return null;
  }

  const defaultMessage = t('whatsapp.defaultMessage');
  const whatsappMessage = message || defaultMessage;
  const whatsappLink = generateWhatsAppLink(config.contact.whatsapp, whatsappMessage);

  return (
    <>
      {/* Floating Button */}
      <div
        className={`fixed right-6 ${position} z-50 transition-all duration-300 ease-in-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div className="relative">
          {/* Tooltip */}
          {showTooltip && (
            <div className="absolute bottom-full right-0 mb-3 w-56">
              <div className="bg-white shadow-xl rounded-lg p-4 relative border border-gray-200">
                <button
                  onClick={() => setShowTooltip(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={t('whatsapp.closeMessage')}
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="text-sm text-gray-700 pr-6">
                  {t('whatsapp.tooltip')}
                </p>
                {/* Arrow */}
                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-200 transform rotate-45" />
              </div>
            </div>
          )}

          {/* WhatsApp Button */}
          <Button
            asChild
            size="lg"
            className="bg-green-500 hover:bg-green-600 text-white rounded-full h-14 w-14 shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-110 group"
            aria-label={t('whatsapp.contactLabel')}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => {
              // Only hide if it's not the auto-show tooltip
              const timer = setTimeout(() => setShowTooltip(false), 100);
              return () => clearTimeout(timer);
            }}
          >
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center"
            >
              <MessageCircle className="h-7 w-7 group-hover:scale-110 transition-transform" />
            </a>
          </Button>

          {/* Pulsing Ring Animation */}
          <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20" />
        </div>
      </div>
    </>
  );
};
